"use client";

import Bowser from "bowser";
import {
  createContext,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { DeviceContextValue, DeviceInfo, DeviceKind } from "@/types";

export const DeviceContext = createContext<DeviceContextValue | null>(null);

const UNKNOWN_DEVICE: DeviceInfo = { kind: "unknown" };

type DeviceClientProviderProps = {
  device?: DeviceInfo;
  children: ReactNode;
};

// Client-detected device — SSR renders with "unknown", client resolves on mount
// via bowser. Avoids reading headers() during render (incompatible with
// cacheComponents).
export function DeviceClientProvider({
  device,
  children,
}: DeviceClientProviderProps) {
  const [resolved, setResolved] = useState<DeviceInfo>(
    device ?? UNKNOWN_DEVICE,
  );

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = navigator.userAgent;
    const parsed = Bowser.parse(ua);
    const platformType = parsed.platform?.type as DeviceKind | undefined;
    const kind: DeviceKind = platformType ?? "desktop";
    setResolved({ kind, parsed });
  }, []);

  const value = useMemo<DeviceContextValue>(
    () => ({ device: resolved }),
    [resolved],
  );

  return (
    <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
  );
}
